// src/pages/AdminExplorerPlusAnalytics.tsx
import React, { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { supabase } from '../services/supabaseClient';
import { trackMarketingEvent } from '../lib/marketingEvents';
import {
  FaChartLine,
  FaDownload,
  FaEye,
  FaMousePointer,
  FaPlane,
  FaRedo,
  FaSearch,
  FaShieldAlt,
} from 'react-icons/fa';

type RangeKey = '7d' | '30d' | '90d';

type ExplorerPlusEvent = {
  event_type?: string;
  event_name?: string;
  label?: string;
  source?: string;
  path?: string;
  created_at?: string;
  timestamp?: number;
  metadata?: Record<string, any>;
  category?: string;
  action?: string;
};

type MarketingAnalyticsResponse = {
  ok?: boolean;
  recent_events?: ExplorerPlusEvent[];
  summary?: Record<string, number>;
  error?: string;
};

const RANGE_OPTIONS: Array<{ value: RangeKey; label: string }> = [
  { value: '7d', label: '7 jours' },
  { value: '30d', label: '30 jours' },
  { value: '90d', label: '90 jours' },
];

const CATEGORY_LABELS: Record<string, string> = {
  hebergements_locaux: 'Hébergements locaux',
  monde: 'Vols mondiaux',
  autos: 'Location de voitures',
  experiences: 'Activités & excursions',
  esim: 'eSIM voyage',
};

const ACTION_LABELS: Record<string, string> = {
  page_view: 'Vue de page',
  search_submit: 'Recherche',
  preset_apply: 'Raccourci rapide',
  partner_widget_mount: 'Module partenaire chargé',
  partner_widget_retry: 'Réessai module partenaire',
};

const LOCAL_STORAGE_KEY = 'forfeo_explorer_plus_events_v1';

function formatNumber(value: number | undefined) {
  return new Intl.NumberFormat('fr-CA').format(value || 0);
}

function getCategory(event: ExplorerPlusEvent) {
  return event.metadata?.category || event.category || 'unknown';
}

function getAction(event: ExplorerPlusEvent) {
  return event.metadata?.action || event.action || event.event_name?.replace('explorer_plus_', '') || event.event_type || 'event';
}

function isExplorerPlusEvent(event: ExplorerPlusEvent) {
  return (
    event.metadata?.module === 'explorer_plus' ||
    event.event_name?.startsWith('explorer_plus_') ||
    event.path?.includes('/explorer-plus') ||
    Boolean(event.category && event.action)
  );
}

function readLocalExplorerEvents(): ExplorerPlusEvent[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function downloadJson(filename: string, payload: unknown) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function AdminExplorerPlusAnalytics() {
  const [range, setRange] = useState<RangeKey>('30d');
  const [events, setEvents] = useState<ExplorerPlusEvent[]>([]);
  const [localEvents, setLocalEvents] = useState<ExplorerPlusEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const allEvents = useMemo(() => {
    const combined = [...events, ...localEvents];
    const seen = new Set<string>();
    return combined.filter((event) => {
      const key = `${event.created_at || event.timestamp || ''}-${event.event_name || event.action || ''}-${event.label || ''}-${getCategory(event)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [events, localEvents]);

  const stats = useMemo(() => {
    const byCategory = allEvents.reduce<Record<string, number>>((acc, event) => {
      const key = getCategory(event);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const byAction = allEvents.reduce<Record<string, number>>((acc, event) => {
      const key = getAction(event);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const searches = allEvents.filter((event) => getAction(event) === 'search_submit').length;
    const partnerLoads = allEvents.filter((event) => getAction(event) === 'partner_widget_mount').length;
    const appWebview = allEvents.filter((event) => event.metadata?.is_app_webview).length;

    return {
      total: allEvents.length,
      searches,
      partnerLoads,
      appWebview,
      byCategory,
      byAction,
    };
  }, [allEvents]);

  const loadAnalytics = async () => {
    setLoading(true);
    setMessage(null);
    setLocalEvents(readLocalExplorerEvents());

    try {
      const { data, error } = await supabase.functions.invoke<MarketingAnalyticsResponse>('admin-marketing-analytics', {
        body: { range },
      });
      if (error) throw error;
      const remoteEvents = (data?.recent_events || []).filter(isExplorerPlusEvent);
      setEvents(remoteEvents);
      if (!remoteEvents.length) {
        setMessage('Aucun événement Explorer Plus distant trouvé pour cette période. Les événements locaux du navigateur sont affichés si disponibles.');
      }
    } catch (error: any) {
      setEvents([]);
      setMessage(error?.message || 'Analytics Supabase indisponibles. Les données locales sont affichées si disponibles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const sendTestEvent = async () => {
    setMessage(null);
    const result: any = await trackMarketingEvent({
      event_type: 'explorer_plus_action',
      event_name: 'explorer_plus_admin_test',
      label: 'Test admin Explorer Plus',
      source: 'admin_explorer_plus_analytics',
      metadata: {
        module: 'explorer_plus',
        category: 'admin_test',
        action: 'admin_test',
        device_type: 'admin',
      },
    });

    if (result?.ok) {
      setMessage('Événement test envoyé. Rafraîchis dans quelques secondes.');
      setTimeout(loadAnalytics, 900);
    } else {
      setMessage('Le test a été tenté, mais la fonction de tracking n’a pas confirmé l’enregistrement.');
    }
  };

  const cards = [
    { label: 'Événements Explorer Plus', value: stats.total, icon: <FaChartLine />, tone: 'blue' },
    { label: 'Recherches', value: stats.searches, icon: <FaSearch />, tone: 'emerald' },
    { label: 'Modules partenaires chargés', value: stats.partnerLoads, icon: <FaPlane />, tone: 'indigo' },
    { label: 'Vues app / WebView', value: stats.appWebview, icon: <FaEye />, tone: 'cyan' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <Header />

      <main className="mx-auto max-w-7xl px-4 py-10 md:px-6 lg:px-8">
        <section className="relative overflow-hidden rounded-[2rem] border border-slate-800 bg-slate-950 p-6 text-white shadow-2xl shadow-slate-900/20 md:p-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.35),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.26),transparent_35%)]" />
          <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.28em] text-cyan-200">
                <FaMousePointer /> Explorer Plus Analytics
              </div>
              <h1 className="text-3xl font-black tracking-tight md:text-5xl">Suivre les recherches et clics affiliés.</h1>
              <p className="mt-4 max-w-3xl text-sm font-medium leading-7 text-slate-300 md:text-base">
                Ce tableau donne une vue opérationnelle sur les vues, recherches, raccourcis et modules partenaires Explorer Plus. Il utilise la fonction marketing Supabase existante et conserve un filet local si le backend n’est pas encore prêt.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <select
                value={range}
                onChange={(event) => setRange(event.target.value as RangeKey)}
                className="rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm font-black text-slate-900 outline-none"
              >
                {RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <button type="button" onClick={loadAnalytics} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 shadow-lg transition hover:-translate-y-0.5">
                <FaRedo /> Rafraîchir
              </button>
              <button type="button" onClick={() => downloadJson('forfeo-explorer-plus-events.json', allEvents)} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-5 py-3 text-sm font-black text-white transition hover:-translate-y-0.5 hover:bg-white/15">
                <FaDownload /> Export
              </button>
            </div>
          </div>
        </section>

        {message && (
          <div className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm font-bold text-amber-900">
            {message}
          </div>
        )}

        <section className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {cards.map((card) => (
            <div key={card.label} className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-lg">
                {card.icon}
              </div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">{card.label}</p>
              <p className="mt-2 text-3xl font-black text-slate-950">{formatNumber(card.value)}</p>
            </div>
          ))}
        </section>

        <section className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-black">Répartition par service</h2>
            <div className="mt-5 space-y-3">
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
                const value = stats.byCategory[key] || 0;
                const pct = stats.total ? Math.round((value / stats.total) * 100) : 0;
                return (
                  <div key={key}>
                    <div className="mb-2 flex items-center justify-between text-sm font-black">
                      <span>{label}</span>
                      <span>{formatNumber(value)} · {pct}%</span>
                    </div>
                    <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-blue-600" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-black">Actions clés</h2>
            <div className="mt-5 space-y-3">
              {Object.entries(stats.byAction).slice(0, 8).map(([action, value]) => {
                const pct = stats.total ? Math.round((value / stats.total) * 100) : 0;
                return (
                  <div key={action} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-black text-slate-900">{ACTION_LABELS[action] || action}</span>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-slate-600">{formatNumber(value)} · {pct}%</span>
                    </div>
                  </div>
                );
              })}
              {!Object.keys(stats.byAction).length && <p className="text-sm font-semibold text-slate-500">Aucune action Explorer Plus détectée pour le moment.</p>}
            </div>
          </div>
        </section>

        <section className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-black">Événements récents</h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">Derniers événements liés à Explorer Plus, incluant les événements locaux du navigateur.</p>
            </div>
            <button type="button" onClick={sendTestEvent} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white">
              <FaShieldAlt /> Envoyer un test
            </button>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
              <thead>
                <tr className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  <th className="py-3 pr-4">Moment</th>
                  <th className="py-3 pr-4">Service</th>
                  <th className="py-3 pr-4">Action</th>
                  <th className="py-3 pr-4">Libellé</th>
                  <th className="py-3 pr-4">Source</th>
                  <th className="py-3 pr-4">App</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allEvents.slice(0, 30).map((event, index) => (
                  <tr key={`${event.created_at || event.timestamp || index}-${index}`} className="font-semibold text-slate-700">
                    <td className="py-3 pr-4 whitespace-nowrap">{event.created_at ? new Date(event.created_at).toLocaleString('fr-CA') : event.timestamp ? new Date(event.timestamp).toLocaleString('fr-CA') : '—'}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{CATEGORY_LABELS[getCategory(event)] || getCategory(event)}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{ACTION_LABELS[getAction(event)] || getAction(event)}</td>
                    <td className="py-3 pr-4 min-w-[220px]">{event.label || event.metadata?.category_label || '—'}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{event.source || event.metadata?.source || '—'}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{event.metadata?.is_app_webview ? 'Oui' : 'Non'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!allEvents.length && !loading && (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm font-bold text-slate-500">
                Aucun événement disponible. Visite Explorer Plus, effectue une recherche, puis reviens rafraîchir ce tableau.
              </div>
            )}
            {loading && <p className="py-6 text-sm font-bold text-slate-500">Chargement des données...</p>}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
